import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { ToastContainerComponent, ToastProps, ToastVariant } from '../components/ui/Toast';

interface ToastContextValue {
  show: (variant: ToastVariant, title: string, message?: string, duration?: number) => void;
  success: (title: string, message?: string, duration?: number) => void;
  error: (title: string, message?: string, duration?: number) => void;
  info: (title: string, message?: string, duration?: number) => void;
  warning: (title: string, message?: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

interface ToastWithId extends ToastProps {
  id: string;
}

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastWithId[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const show = useCallback((variant: ToastVariant, title: string, message?: string, duration?: number) => {
    const id = Math.random().toString(36).substring(2, 9);
    const newToast: ToastWithId = {
      id,
      variant,
      title,
      message,
      duration: duration ?? 4000,
      onClose: removeToast
    };

    setToasts(prev => [...prev, newToast]);
  }, [removeToast]);

  const success = useCallback((title: string, message?: string, duration?: number) => {
    show('success', title, message, duration);
  }, [show]);

  const error = useCallback((title: string, message?: string, duration?: number) => {
    show('error', title, message, duration);
  }, [show]);

  const info = useCallback((title: string, message?: string, duration?: number) => {
    show('info', title, message, duration);
  }, [show]);

  const warning = useCallback((title: string, message?: string, duration?: number) => {
    show('warning', title, message, duration);
  }, [show]);

  const value: ToastContextValue = {
    show,
    success,
    error,
    info,
    warning
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainerComponent toasts={toasts} onClose={removeToast} />
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default ToastProvider;
